import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill, formatCurrency, formatDate } from "@/lib/utils-format";
import { Plus, MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";
import { downloadCsv } from "@/lib/exports";

const STATUSES = ["all", "New Order", "Waiting for Payment", "Payment Under Review", "Payment Confirmed", "Ready to Ship", "Shipped", "Delivered", "Cancelled"];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    api.get("/orders", { params })
      .then((r) => setOrders(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [status]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const filtered = useMemo(() => orders, [orders]);

  return (
    <div className="p-8" data-testid="orders-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Operations</p>
          <h1 className="text-4xl font-black tracking-tighter">Orders</h1>
        </div>
        <Button onClick={() => navigate("/orders/new")} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="create-order-button">
          <Plus size={16} weight="bold" className="mr-1" /> New Order
        </Button>
      </div>

      {/* Filters */}
      <form onSubmit={onSearch} className="flex gap-3 mb-4">
        <div className="flex-1 relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Order #, customer, email, phone, tracking, payment ref…"
            className="pl-9 border-zinc-300 rounded-md" data-testid="orders-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[220px] border-zinc-300 rounded-md" data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button type="submit" variant="outline" className="border-zinc-300 rounded-md" data-testid="orders-search-button">Search</Button>
        <Button type="button" variant="outline" onClick={() => downloadCsv("/exports/orders", "orders.csv")}
          className="border-zinc-300 rounded-md" data-testid="orders-export-button">
          <DownloadSimple size={14} weight="bold" className="mr-1" /> Export CSV
        </Button>
      </form>

      {/* Table */}
      <div className="border border-zinc-200 bg-white overflow-hidden" data-testid="orders-table">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2.5">Order #</th>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Customer</th>
              <th className="text-left px-4 py-2.5">Address</th>
              <th className="text-left px-4 py-2.5">Product</th>
              <th className="text-right px-4 py-2.5">Amount</th>
              <th className="text-left px-4 py-2.5">Payment</th>
              <th className="text-left px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center text-sm text-zinc-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-sm text-zinc-500">No orders found.</td></tr>
            ) : filtered.map((o) => (
              <tr key={o.order_id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/orders/${o.order_id}`)} data-testid={`order-row-${o.order_number}`}>
                <td className="px-4 py-3 font-mono text-sm">
                  <Link to={`/orders/${o.order_id}`} className="hover:underline">{o.order_number}</Link>
                  {o.external_order_number && (
                    <div className="text-[10px] text-zinc-500 mt-0.5">form #{o.external_order_number}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-zinc-600">{formatDate(o.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium">{o.customer_name}</div>
                  <div className="text-xs text-zinc-500">{o.email || o.phone || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs text-zinc-700 max-w-[220px] truncate" title={o.address || ""}>
                    {o.address || "—"}
                  </div>
                  <div className="text-[10px] text-zinc-500">{o.country || ""}</div>
                </td>
                <td className="px-4 py-3 text-sm">
                  <div>{o.product}</div>
                  <div className="text-xs text-zinc-500">Qty {o.quantity}</div>
                </td>
                <td className="px-4 py-3 text-sm font-mono text-right">{formatCurrency(o.total_amount, o.currency)}</td>
                <td className="px-4 py-3 text-xs text-zinc-600">
                  {o.payment_method}
                  {o.payment_reference && (
                    <div className="text-[10px] text-zinc-500 font-mono truncate max-w-[120px]" title={o.payment_reference}>
                      ref: {o.payment_reference}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3"><StatusPill status={o.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
